import React from 'react';

import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import EditOffIcon from '@mui/icons-material/EditOff';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import LayersIcon from '@mui/icons-material/Layers';

import { CartContext } from '../../cart/CartHandler';
import CartItem, { RemainderItem, HardwareItem, aggregateCabinetHardware } from '../../cart/CartItem';
import EdgingIcon from '../common/EdgingIcon';
import NumericInput from '../../common/inputs/NumericInput';
import DesktopInstacartTableRow from './DesktopInstacartTableRow';

// ── Mobile card ────────────────────────────────────────────────────────────

interface UnifiedInstacartItemCardProps {
    isRemainder?: boolean;
    isChildCard?: boolean;
    productImage?: React.ReactNode;
    edgingIcon?: React.ReactNode;
    name: string;
    materialName?: string;
    isDuplunged?: boolean;
    dimensions?: string;
    price?: number;
    count: number;
    updateCount?: (newCount: number) => void;
    open?: boolean;
    isEditing?: boolean;
    expandClick?: () => void;
    deleteItem?: () => void;
    editItem?: () => void;
    disabled?: boolean;
}

const UnifiedInstacartItemCard: React.FC<UnifiedInstacartItemCardProps> = (props) => {
    const spacing = props.isChildCard ? 1 : 1.5;
    const padding = props.isChildCard ? 0.5 : 1;

    const infoRows = (
        <Grid container spacing={spacing} alignItems="center" sx={{ padding }}>
            <Grid size={{ xs: 3 }}>
                <Typography variant="caption">Név:</Typography>
            </Grid>
            <Grid size={{ xs: 9 }}>
                <Typography variant="subtitle2">{props.name}</Typography>
            </Grid>

            {props.materialName !== undefined && (
                <>
                    <Grid size={{ xs: 3 }}>
                        <Typography variant="caption">Anyag:</Typography>
                    </Grid>
                    <Grid size={{ xs: 9 }}>
                        <Box display="flex" alignItems="center">
                            <Box sx={{ flexShrink: 0, flexGrow: 0, display: 'flex', alignItems: 'center', mr: 1, '& > *': { mr: '0 !important' } }}>
                                {props.edgingIcon ?? <></>}
                                {props.isDuplunged && <LayersIcon />}
                            </Box>
                            <Typography variant="subtitle2" sx={{ flex: 1, minWidth: 0 }}>
                                {props.materialName}
                            </Typography>
                        </Box>
                    </Grid>
                </>
            )}

            {props.dimensions !== undefined && (
                <>
                    <Grid size={{ xs: 3 }}>
                        <Typography variant="caption">Méret:</Typography>
                    </Grid>
                    <Grid size={{ xs: 9 }}>
                        <Typography variant="subtitle2">
                            {props.isRemainder
                                ? (Number(props.dimensions) / 10000).toFixed(2) + 'm²'
                                : props.dimensions + 'mm'}
                        </Typography>
                    </Grid>
                </>
            )}

            {props.price !== undefined && (
                <>
                    <Grid size={{ xs: 3 }}>
                        <Typography variant="caption">Ár:</Typography>
                    </Grid>
                    <Grid size={{ xs: 9 }}>
                        <Typography variant="subtitle2">{props.price} Ft</Typography>
                    </Grid>
                </>
            )}
        </Grid>
    );

    const quantitySection = props.updateCount ? (
        <Box display="flex" alignItems="center" gap={1} sx={{ mt: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <NumericInput
                    value={props.count}
                    min={1}
                    max={1000}
                    smallStep={1}
                    largeStep={1}
                    editable={!props.disabled}
                    readOnly={props.disabled ?? false}
                    onValueChanged={(newValue: number) => props.updateCount!(newValue)}
                    unitLabel="db"
                    slotProps={{
                        textField: {
                            sx: {
                                width: '100%',
                                mt: 0.5,
                                backgroundColor: '#ffffff !important',
                                borderRadius: '4px 0 0 4px',
                                '& .MuiInputBase-root': { backgroundColor: '#ffffff !important' },
                                '& .MuiInputBase-input': {
                                    color: '#000 !important',
                                    WebkitTextFillColor: '#000 !important',
                                },
                            },
                        },
                        buttonGroupButton: {
                            sx: { minWidth: '48px' },
                            color: '#fff !important',
                            backgroundColor: '#3e5c4b !important',
                            borderColor: '#000 !important',
                            '&:hover': { backgroundColor: '#314c3d !important' },
                        },
                    }}
                />
            </Box>
            {props.editItem !== undefined && props.deleteItem !== undefined && (
                <ButtonGroup color="secondary" disabled={props.disabled} sx={{ mt: 0.5 }}>
                    <Button>
                        {props.isEditing
                            ? <EditOffIcon onClick={props.editItem!} />
                            : <EditIcon onClick={props.editItem!} />}
                    </Button>
                    <Button>
                        <DeleteIcon onClick={props.deleteItem!} />
                    </Button>
                </ButtonGroup>
            )}
        </Box>
    ) : (
        <Grid container spacing={spacing} alignItems="center" sx={{ padding }}>
            <Grid size={{ xs: 3 }}>
                <Typography variant="caption">Mennyiség:</Typography>
            </Grid>
            <Grid size={{ xs: 9 }}>
                <Typography variant="subtitle2">{props.count} db</Typography>
            </Grid>
            {props.editItem !== undefined && props.deleteItem !== undefined && (
                <>
                    <Grid size={{ xs: 3 }}>
                        <Typography variant="caption">Műveletek:</Typography>
                    </Grid>
                    <Grid size={{ xs: 9 }}>
                        <ButtonGroup color="secondary" disabled={props.disabled}>
                            <Button>
                                {props.isEditing
                                    ? <EditOffIcon onClick={props.editItem!} />
                                    : <EditIcon onClick={props.editItem!} />}
                            </Button>
                            <Button>
                                <DeleteIcon onClick={props.deleteItem!} />
                            </Button>
                        </ButtonGroup>
                    </Grid>
                </>
            )}
        </Grid>
    );

    return (
        <Card sx={{ overflow: 'hidden' }}>
            <Box sx={{ p: props.isChildCard ? 0.75 : 1.25 }}>
                {props.productImage ? (
                    <Box display="flex" gap={1.5} alignItems="flex-start">
                        <Box sx={{ flexShrink: 0 }}>{props.productImage}</Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>{infoRows}</Box>
                    </Box>
                ) : infoRows}
                {!props.isRemainder && quantitySection}
            </Box>
            {props.open !== undefined && (
                <Box
                    onClick={props.expandClick}
                    sx={{
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        py: 0.75,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.5,
                        cursor: 'pointer',
                        userSelect: 'none',
                    }}
                >
                    <Typography variant="caption" sx={{ color: 'inherit', fontWeight: 600 }}>
                        {props.open ? 'Részletek rejtése' : 'Részletek mutatása'}
                    </Typography>
                    {props.open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </Box>
            )}
        </Card>
    );
};

// ── Per-item wrapper ───────────────────────────────────────────────────────

interface UnifiedInstacartItemProps {
    cartItem: CartItem;
    isMobileView: boolean;
    itemIndex: number;
    mobileOpenIndex: number | null;
    onMobileOpen: () => void;
    updateCount: (newCount: number) => void;
    deleteItem: () => void;
    editItem: () => void;
    disabled?: boolean;
}

const UnifiedInstacartItem: React.FC<UnifiedInstacartItemProps> = (props) => {
    const cartContext = React.useContext(CartContext);
    const [open, setOpen] = React.useState(!props.isMobileView);

    React.useEffect(() => {
        setOpen(!props.isMobileView);
    }, [props.isMobileView]);

    React.useEffect(() => {
        if (props.isMobileView && props.mobileOpenIndex !== null && props.mobileOpenIndex !== props.itemIndex) {
            setOpen(false);
        }
    }, [props.mobileOpenIndex]);

    const handleExpandClick = () => {
        const newOpen = !open;
        setOpen(newOpen);
        if (props.isMobileView && newOpen) {
            props.onMobileOpen();
        }
    };
    const isRemainder = props.cartItem instanceof RemainderItem;
    const price = props.cartItem.PriceGross * props.cartItem.count;
    const hardware = aggregateCabinetHardware(props.cartItem, cartContext.hardwarePlan);

    // Hardware bag line: flat, read-only, no children.
    if (props.cartItem instanceof HardwareItem) {
        const hw = props.cartItem;
        const bagCount = hw.bags.reduce((sum, bag) => sum + bag.qty, 0);
        const subLabel = hw.error !== null
            ? 'Ár nem elérhető'
            : hw.bags.map(bag => `${bag.qty} × ${bag.pack_size} db`).join(' + ')
                + ` · ${hw.needed} felhasználva · ${hw.leftover} tartalék`;
        const hwPrice = hw.error !== null ? undefined : hw.PriceGross;

        return props.isMobileView
            ? <UnifiedInstacartItemCard
                productImage={hw.productImage
                    ? <Avatar src={hw.productImage} variant="rounded" sx={{ width: 60, height: 60 }} />
                    : undefined}
                name={hw.Name}
                materialName={subLabel}
                price={hwPrice}
                count={bagCount}
                disabled={props.disabled}
            />
            : <DesktopInstacartTableRow
                key={0}
                productImage={hw.productImage
                    ? <Avatar src={hw.productImage} variant="rounded" sx={{ width: 60, height: 60, marginRight: 5 }} />
                    : undefined}
                name={hw.Name}
                materialName={subLabel}
                price={hwPrice}
                priceFinal={true}
                count={bagCount}
                disabled={props.disabled}
            />;
    }

    if (props.isMobileView) {
        return props.cartItem.children.length === 1
            ? <UnifiedInstacartItemCard
                productImage={props.cartItem.productImage
                    ? <Avatar src={props.cartItem.productImage} variant="rounded" sx={{ width: 60, height: 60 }} />
                    : undefined}
                edgingIcon={<EdgingIcon productItem={props.cartItem.children[0]} descriptor={props.cartItem.descriptor} />}
                name={props.cartItem.Name}
                materialName={props.cartItem.children[0].sheetMaterialName}
                isDuplunged={props.cartItem.children[0].Duplung !== undefined}
                dimensions={props.cartItem.Dimensions}
                price={price}
                count={props.cartItem.count}
                updateCount={isRemainder ? undefined : props.updateCount}
                isEditing={props.cartItem === cartContext.editedItem}
                editItem={isRemainder ? undefined : props.editItem}
                deleteItem={isRemainder ? undefined : props.deleteItem}
                disabled={props.disabled}
            />
            : <Box>
                <UnifiedInstacartItemCard
                    isRemainder={isRemainder}
                    productImage={!isRemainder && props.cartItem.productImage
                        ? <Avatar src={props.cartItem.productImage} variant="rounded" sx={{ width: 60, height: 60 }} />
                        : undefined}
                    edgingIcon={isRemainder && (props.cartItem as RemainderItem).image
                        ? <Avatar src={(props.cartItem as RemainderItem).image} variant="rounded" sx={{ width: 60, height: 60 }} />
                        : undefined}
                    name={isRemainder ? 'Maradék' : props.cartItem.Name}
                    materialName={isRemainder ? props.cartItem.Name : undefined}
                    dimensions={props.cartItem.Dimensions}
                    price={price}
                    count={props.cartItem.count}
                    open={isRemainder ? undefined : open}
                    isEditing={props.cartItem === cartContext.editedItem}
                    expandClick={isRemainder ? undefined : handleExpandClick}
                    updateCount={isRemainder ? undefined : props.updateCount}
                    editItem={isRemainder ? undefined : props.editItem}
                    deleteItem={isRemainder ? undefined : props.deleteItem}
                    disabled={props.disabled}
                />
                <Collapse in={open} timeout="auto" unmountOnExit>
                    <Box sx={{ ml: 2, borderLeft: '2px solid', borderColor: 'divider', pl: 0.5 }}>
                        <Stack direction="column" spacing={0.5} sx={{ mt: 0.5 }}>
                            {props.cartItem.children.map((childItem, index) => (
                                <UnifiedInstacartItemCard
                                    key={index}
                                    isChildCard
                                    edgingIcon={<EdgingIcon productItem={childItem} descriptor={props.cartItem.descriptor} />}
                                    name={childItem.Name}
                                    materialName={childItem.sheetMaterialName}
                                    isDuplunged={childItem.Duplung !== undefined}
                                    count={1}
                                />
                            ))}
                            {hardware.map((hw, index) => (
                                <UnifiedInstacartItemCard
                                    key={`hw-${index}`}
                                    isChildCard
                                    productImage={hw.productImage
                                        ? <Avatar src={hw.productImage} variant="rounded" sx={{ width: 60, height: 60, marginRight: 5 }} />
                                        : undefined}
                                    name={hw.Name}
                                    materialName={hw.error !== null ? 'Ár nem elérhető' : undefined}
                                    price={!hw.packed && hw.error === null ? hw.PriceGross * hw.count : undefined}
                                    count={hw.count}
                                />
                            ))}
                        </Stack>
                    </Box>
                </Collapse>
            </Box>;
    }

    // Desktop: table rows
    return props.cartItem.children.length === 1
        ? <DesktopInstacartTableRow
            key={0}
            edgingIcon={<EdgingIcon productItem={props.cartItem.children[0]} descriptor={props.cartItem.descriptor} />}
            name={props.cartItem.Name}
            materialName={props.cartItem.children[0].sheetMaterialName}
            isDuplunged={props.cartItem.children[0].Duplung !== undefined}
            dimensions={props.cartItem.Dimensions}
            price={price}
            priceFinal={props.cartItem.priceFinal}
            count={props.cartItem.count}
            isEditing={props.cartItem === cartContext.editedItem}
            updateCount={isRemainder ? undefined : props.updateCount}
            editItem={isRemainder ? undefined : props.editItem}
            deleteItem={isRemainder ? undefined : props.deleteItem}
            disabled={props.disabled}
        />
        : <>
            <DesktopInstacartTableRow
                key={0}
                productImage={props.cartItem.productImage
                    ? <Avatar src={props.cartItem.productImage} variant="rounded" sx={{ width: 60, height: 60, marginRight: 5 }} />
                    : undefined}
                isRemainder={isRemainder}
                edgingIcon={isRemainder && (props.cartItem as RemainderItem).image
                    ? <Avatar src={(props.cartItem as RemainderItem).image} variant="rounded" sx={{ width: 60, height: 60, marginRight: 5 }} />
                    : undefined}
                name={isRemainder ? 'Maradék' : props.cartItem.Name}
                materialName={isRemainder ? props.cartItem.Name : undefined}
                dimensions={props.cartItem.Dimensions}
                price={price}
                priceFinal={props.cartItem.priceFinal}
                count={props.cartItem.count}
                open={isRemainder ? undefined : open}
                isEditing={props.cartItem === cartContext.editedItem}
                expandClick={isRemainder ? undefined : handleExpandClick}
                updateCount={isRemainder ? undefined : props.updateCount}
                editItem={isRemainder ? undefined : props.editItem}
                deleteItem={isRemainder ? undefined : props.deleteItem}
                disabled={props.disabled}
            />
            <TableRow>
                <TableCell style={{ padding: 0 }} colSpan={5}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Table size="small">
                            <colgroup>
                                <col style={{ width: '60%' }} />
                                <col style={{ width: '10%' }} />
                                <col style={{ width: '10%' }} />
                                <col style={{ width: '10%' }} />
                                <col style={{ width: '10%' }} />
                            </colgroup>
                            <TableBody>
                                {props.cartItem.children.map((childItem, index) => (
                                    <DesktopInstacartTableRow
                                        doIndent={true}
                                        key={index}
                                        edgingIcon={<EdgingIcon productItem={childItem} descriptor={props.cartItem.descriptor} />}
                                        name={childItem.Name}
                                        materialName={childItem.sheetMaterialName}
                                        isDuplunged={childItem.Duplung !== undefined}
                                        count={1}
                                    />
                                ))}
                                {hardware.map((hw, index) => (
                                    <DesktopInstacartTableRow
                                        doIndent={true}
                                        key={props.cartItem.children.length + index}
                                        productImage={hw.productImage
                                            ? <Avatar src={hw.productImage} variant="rounded" sx={{ width: 60, height: 60, marginRight: 5 }} />
                                            : undefined}
                                        name={hw.Name}
                                        materialName={hw.error !== null ? 'Ár nem elérhető' : undefined}
                                        price={!hw.packed && hw.error === null ? hw.PriceGross * hw.count : undefined}
                                        count={hw.count}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>;
};

// ── Main export ────────────────────────────────────────────────────────────

const UnifiedInstacartContent: React.FC = () => {
    const cartContext = React.useContext(CartContext);
    const theme = useTheme();
    const isMobileView = useMediaQuery(theme.breakpoints.down('md'));
    const [mobileOpenIndex, setMobileOpenIndex] = React.useState<number | null>(null);

    React.useEffect(() => {
        if (!isMobileView) setMobileOpenIndex(null);
    }, [isMobileView]);

    const items = cartContext.cartItems.length > 0
        ? cartContext.cartItems.map((item, index) => (
            <UnifiedInstacartItem
                key={index}
                cartItem={item}
                isMobileView={isMobileView}
                itemIndex={index}
                mobileOpenIndex={mobileOpenIndex}
                onMobileOpen={() => setMobileOpenIndex(index)}
                updateCount={(newCount) => cartContext.updateItemCount(item, newCount)}
                editItem={() => cartContext.editItem(item)}
                deleteItem={() => cartContext.deleteItem(item)}
                disabled={cartContext.cartLoading}
            />
        ))
        : null;

    return (
        <Box overflow={isMobileView ? undefined : 'auto'} height={isMobileView ? undefined : '100%'} position="relative">
            {isMobileView
                ? <Stack direction="column" spacing={2} sx={{ padding: 1 }}>{items}</Stack>
                : <TableContainer>
                    <Table size="small">
                        <colgroup>
                            <col style={{ width: '60%' }} />
                            <col style={{ width: '10%' }} />
                            <col style={{ width: '10%' }} />
                            <col style={{ width: '10%' }} />
                            <col style={{ width: '10%' }} />
                        </colgroup>
                        <TableHead>
                            <TableRow>
                                <TableCell><Typography variant="h6">Név</Typography></TableCell>
                                <TableCell><Typography variant="h6">Méret</Typography></TableCell>
                                <TableCell><Typography variant="h6">Ár</Typography></TableCell>
                                <TableCell><Typography variant="h6">Mennyiség</Typography></TableCell>
                                <TableCell><Typography variant="h6">Műveletek</Typography></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>{items}</TableBody>
                    </Table>
                  </TableContainer>
            }
            {cartContext.cartLoading && (
                <Box
                    position="absolute"
                    top={0} left={0} right={0} bottom={0}
                    bgcolor="rgba(255, 255, 255, 0.8)"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    zIndex={1000}
                >
                    <Typography variant="h6">Türelmét kérjük, ár számolás folyamatban...</Typography>
                </Box>
            )}
            {cartContext.cartSaving && (
                <Box
                    position="absolute"
                    top={0} left={0} right={0} bottom={0}
                    bgcolor="rgba(255, 255, 255, 0.85)"
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    justifyContent="center"
                    alignItems="center"
                    zIndex={1001}
                >
                    <CircularProgress />
                    <Typography variant="h6">Kosár mentése, türelmét kérjük...</Typography>
                </Box>
            )}
        </Box>
    );
};

export default UnifiedInstacartContent;